import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { FiEdit2, FiEye, FiPlus, FiSearch, FiTrash2 } from "react-icons/fi";
import CallCenterModal from "../../components/call-center/listing/CallCenterModal";
import { toast } from "react-toastify";
import useCallCenterT from "../../hooks/useCallCenterT";
import { isCallingEnabled } from "../../utils/orgFeatures";
import { hasScreenAccess } from "../../utils/permissions";
import {
  createCampaign,
  deleteCampaign,
  getCampaignDashboard,
  listCampaignTasks,
  listCampaigns,
  updateCampaign,
} from "../../service/CallCenterCampaigns";
import CampaignFormModal from "../../components/call-center/CampaignFormModal";
import ConfirmDialog from "../../components/call-center/listing/ConfirmDialog";
import ListingEmptyState from "../../components/call-center/listing/ListingEmptyState";
import ListingFilterHeader from "../../components/call-center/listing/ListingFilterHeader";
import ListingPaginationFooter from "../../components/call-center/listing/ListingPaginationFooter";
import StatusBadge from "../../components/call-center/listing/StatusBadge";
import TableSkeleton from "../../components/call-center/listing/TableSkeleton";
import {
  FILTER_FIELD_CLASS,
  FILTER_LABEL_CLASS,
  LISTING_CARD_CLASS,
  LISTING_CONTAINER_CLASS,
  LISTING_FILTER_CARD_CLASS,
  LISTING_ICON_ACTION_CLASS,
  LISTING_ICON_ACTION_DANGER_CLASS,
  LISTING_PAGE_CLASS,
  LISTING_PRIMARY_BUTTON_CLASS,
  LISTING_TABLE_HEAD_CLASS,
  LISTING_TABLE_HEADER_CELL_CLASS,
  LISTING_TABLE_ROW_CLASS,
} from "../../components/call-center/listing/listingStyles";

const PAGE_SIZE = 20;

const formatCallTaskStatus = (status, t) => {
  if (!status) return t("ccEmDash");
  return t(`ccCallTaskStatus_${status}`);
};

const formatAiCallIntent = (intent, t) => {
  if (!intent) return t("ccEmDash");
  return t(`ccAiCallIntent_${intent}`);
};

const formatTimestamp = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
};

const formatRecovery = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  return `${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} OMR`;
};

const CallCenterCampaignsPage = () => {
  const t = useCallCenterT();
  const canCreate = hasScreenAccess("CC_CAMPAIGNS", "canCreate");
  const canUpdate = hasScreenAccess("CC_CAMPAIGNS", "canUpdate");
  const canDelete = hasScreenAccess("CC_CAMPAIGNS", "canDelete");

  const [campaigns, setCampaigns] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [page, setPage] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [detailCampaign, setDetailCampaign] = useState(null);
  const [detailTasks, setDetailTasks] = useState([]);
  const [detailTasksLoading, setDetailTasksLoading] = useState(false);

  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await listCampaigns();
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (loadError) {
      setError(loadError?.message || t("ccToastLoadCampaignsFailed"));
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadDashboard = useCallback(async () => {
    try {
      setDashboardLoading(true);
      const data = await getCampaignDashboard();
      setDashboard(data || null);
    } catch {
      setDashboard(null);
    } finally {
      setDashboardLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isCallingEnabled()) return;
    loadCampaigns();
    loadDashboard();
  }, [loadCampaigns, loadDashboard]);

  const filteredCampaigns = useMemo(() => {
    const query = search.trim().toLowerCase();
    return campaigns.filter((campaign) => {
      if (query && !String(campaign.name || "").toLowerCase().includes(query)) {
        return false;
      }
      if (activeFilter === "active" && !campaign.active) return false;
      if (activeFilter === "inactive" && campaign.active) return false;
      return true;
    });
  }, [campaigns, search, activeFilter]);

  const totalElements = filteredCampaigns.length;
  const totalPages = Math.max(Math.ceil(totalElements / PAGE_SIZE), totalElements > 0 ? 1 : 0);
  const pagedCampaigns = useMemo(() => {
    const start = page * PAGE_SIZE;
    return filteredCampaigns.slice(start, start + PAGE_SIZE);
  }, [filteredCampaigns, page]);

  useEffect(() => {
    setPage(0);
  }, [search, activeFilter]);

  const hasActiveFilters = Boolean(search || activeFilter);

  const applyFilters = () => setSearch(searchInput.trim());
  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setActiveFilter("");
    setPage(0);
  };

  const openCreateForm = () => {
    setEditingCampaign(null);
    setFormOpen(true);
  };

  const openEditForm = (campaign) => {
    setEditingCampaign(campaign);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingCampaign(null);
  };

  const handleSaveCampaign = async (payload) => {
    try {
      setSaving(true);
      if (editingCampaign?.id) {
        await updateCampaign(editingCampaign.id, payload);
        toast.success(t("ccToastCampaignUpdated"));
      } else {
        await createCampaign(payload);
        toast.success(t("ccToastCampaignLaunched"));
      }
      closeForm();
      await Promise.all([loadCampaigns(), loadDashboard()]);
    } catch (saveError) {
      toast.error(saveError?.message || t("ccToastCampaignCreateFailed"));
      throw saveError;
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleting(true);
      await deleteCampaign(deleteTarget.id);
      toast.success(t("ccToastCampaignDeleted"));
      setDeleteTarget(null);
      if (detailCampaign?.id === deleteTarget.id) {
        setDetailCampaign(null);
        setDetailTasks([]);
      }
      await Promise.all([loadCampaigns(), loadDashboard()]);
    } catch (deleteError) {
      toast.error(deleteError?.message || t("ccToastCampaignDeleteFailed"));
    } finally {
      setDeleting(false);
    }
  };

  const openDetail = async (campaign) => {
    setDetailCampaign(campaign);
    setDetailTasks([]);
    setDetailTasksLoading(true);
    try {
      const tasks = await listCampaignTasks(campaign.id);
      setDetailTasks(Array.isArray(tasks) ? tasks : []);
    } catch {
      toast.error(t("ccCampaignTasksLoadFailed"));
      setDetailTasks([]);
    } finally {
      setDetailTasksLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailCampaign(null);
    setDetailTasks([]);
  };

  useEffect(() => {
    if (!detailCampaign) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeDetail();
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [detailCampaign]);

  if (!isCallingEnabled()) {
    return <Navigate to="/call-center/dashboard" replace />;
  }

  return (
    <div className={LISTING_PAGE_CLASS}>
      <div className={LISTING_CONTAINER_CLASS}>
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-[28px] font-bold tracking-tight text-[#111827]">
              {t("ccCampaignManagerTitle")}
            </h1>
            <p className="mt-1 text-sm text-[#6b7280]">{t("ccCampaignPaymentReminderPageSubtitle")}</p>
          </div>
          {canCreate ? (
            <button type="button" onClick={openCreateForm} className={LISTING_PRIMARY_BUTTON_CLASS}>
              <FiPlus className="h-4 w-4" />
              {t("ccCreateCampaign")}
            </button>
          ) : null}
        </header>

        <div className="mb-6 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-900">{t("ccCampaignFlowTitle")}</h2>
          <p className="mt-1 text-sm text-slate-600">{t("ccCampaignPaymentReminderPageIntro")}</p>
        </div>

        <section className="mb-6">
          <p className="mb-3 text-xs text-slate-500">{t("ccCampaignKpiAiScopeHint")}</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[
            {
              label: t("ccCampaignKpiAiCalls"),
              value: dashboardLoading ? "…" : Number(dashboard?.totalCalls || 0).toLocaleString(),
            },
            {
              label: t("ccCampaignKpiAiAnsweredRate"),
              value: dashboardLoading
                ? "…"
                : `${Number(dashboard?.answeredPercentage || 0).toFixed(1)}%`,
            },
            {
              label: t("ccCampaignKpiAiSuccessRate"),
              value: dashboardLoading
                ? "…"
                : `${Number(dashboard?.successPercentage || 0).toFixed(1)}%`,
            },
            {
              label: t("ccCampaignKpiAiRecovery"),
              value: dashboardLoading ? "…" : formatRecovery(dashboard?.recoveryAmount),
            },
          ].map((card) => (
            <div
              key={card.label}
              className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm"
            >
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                {card.label}
              </p>
              <p className="mt-2 text-2xl font-bold text-slate-900">{card.value}</p>
            </div>
          ))}
          </div>
        </section>

        {error ? (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <span>{error}</span>
            <button type="button" onClick={loadCampaigns} className="font-semibold hover:underline">
              {t("ccRefresh")}
            </button>
          </div>
        ) : null}

        <section className={LISTING_FILTER_CARD_CLASS}>
          <ListingFilterHeader
            title={t("ccListFiltersTitle")}
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            resultsLabel={t("ccCampaignResultsLabel")}
            showingPageLabel={t("ccShowingPage")}
            ofLabel={t("ccOf")}
            refreshLabel={t("ccRefresh")}
            loading={loading}
            onRefresh={loadCampaigns}
            actions={
              hasActiveFilters ? (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="text-sm font-medium text-slate-600 hover:text-slate-900"
                >
                  {t("ccClearFilters")}
                </button>
              ) : null
            }
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="md:col-span-2">
              <label className={FILTER_LABEL_CLASS}>{t("ccSearch")}</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") applyFilters();
                    }}
                    placeholder={t("ccCampaignSearchPlaceholder")}
                    className={`${FILTER_FIELD_CLASS} pl-9`}
                  />
                </div>
                <button type="button" onClick={applyFilters} className={LISTING_PRIMARY_BUTTON_CLASS}>
                  {t("ccSearch")}
                </button>
              </div>
            </div>
            <div>
              <label className={FILTER_LABEL_CLASS}>{t("ccStatus")}</label>
              <select
                value={activeFilter}
                onChange={(event) => setActiveFilter(event.target.value)}
                className={FILTER_FIELD_CLASS}
              >
                <option value="">{t("ccAll")}</option>
                <option value="active">{t("ccCampaignStatusActive")}</option>
                <option value="inactive">{t("ccCampaignStatusInactive")}</option>
              </select>
            </div>
          </div>
        </section>

        <section className={LISTING_CARD_CLASS}>
          {loading ? (
            <div className="p-4">
              <TableSkeleton columns={5} rows={6} />
            </div>
          ) : !pagedCampaigns.length ? (
            <ListingEmptyState
              icon={FiPlus}
              title={t("ccCampaignsEmptyTitle")}
              hint={t("ccCampaignsEmptyHint")}
              action={
                canCreate ? (
                  <button
                    type="button"
                    onClick={openCreateForm}
                    className={`${LISTING_PRIMARY_BUTTON_CLASS} mt-4`}
                  >
                    {t("ccCreateCampaign")}
                  </button>
                ) : null
              }
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className={LISTING_TABLE_HEAD_CLASS}>
                    <tr>
                      <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccCampaignName")}</th>
                      <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccCampaignTypeLabel")}</th>
                      <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccExecutionScheduleCron")}</th>
                      <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccStatus")}</th>
                      <th className={`${LISTING_TABLE_HEADER_CELL_CLASS} text-right`}>
                        {t("ccActions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedCampaigns.map((campaign) => (
                      <tr key={campaign.id} className={LISTING_TABLE_ROW_CLASS}>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => openDetail(campaign)}
                            className="font-semibold text-slate-900 hover:text-[#2B7FFF]"
                          >
                            {campaign.name}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status="ACTIVE" label={t("ccCampaignTypeAiPaymentReminder")} />
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-600">
                          {campaign.scheduleCron || t("ccEmDash")}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge
                            status={campaign.active ? "ACTIVE" : "CANCELLED"}
                            label={
                              campaign.active
                                ? t("ccCampaignStatusActive")
                                : t("ccCampaignStatusInactive")
                            }
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              title={t("ccViewDetails")}
                              onClick={() => openDetail(campaign)}
                              className={LISTING_ICON_ACTION_CLASS}
                            >
                              <FiEye className="h-4 w-4" />
                            </button>
                            {canUpdate ? (
                              <button
                                type="button"
                                title={t("ccEdit")}
                                onClick={() => openEditForm(campaign)}
                                className={LISTING_ICON_ACTION_CLASS}
                              >
                                <FiEdit2 className="h-4 w-4" />
                              </button>
                            ) : null}
                            {canDelete ? (
                              <button
                                type="button"
                                title={t("ccDelete")}
                                onClick={() => setDeleteTarget(campaign)}
                                className={LISTING_ICON_ACTION_DANGER_CLASS}
                              >
                                <FiTrash2 className="h-4 w-4" />
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ListingPaginationFooter
                totalElements={totalElements}
                resultsLabel={t("ccCampaignResultsLabel")}
                page={page}
                totalPages={totalPages}
                loading={loading}
                onPrevious={() => setPage((value) => Math.max(0, value - 1))}
                onNext={() => setPage((value) => value + 1)}
                previousLabel={t("ccPrevious")}
                nextLabel={t("ccNext")}
              />
            </>
          )}
        </section>

        <CampaignFormModal
          open={formOpen}
          campaign={editingCampaign}
          saving={saving}
          t={t}
          onClose={closeForm}
          onSubmit={handleSaveCampaign}
        />

        <ConfirmDialog
          open={Boolean(deleteTarget)}
          title={t("ccConfirmDialogTitle")}
          message={t("ccCampaignDeleteConfirm").replace("{name}", deleteTarget?.name || "")}
          cancelLabel={t("ccCancel")}
          confirmLabel={t("ccConfirmDialogProceed")}
          processingLabel={t("ccProcessing")}
          variant="danger"
          loading={deleting}
          onConfirm={confirmDelete}
          onClose={() => setDeleteTarget(null)}
        />

        <CallCenterModal
          open={Boolean(detailCampaign)}
          onClose={closeDetail}
          title={detailCampaign?.name}
          subtitle={t("ccCampaignDetailPaymentReminderSubtitle")}
          maxWidth="3xl"
          maxHeight="90vh"
          closeLabel={t("ccClose")}
          contentVariant="none"
          bodyClassName="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 space-y-6"
          footer={
            canUpdate && detailCampaign ? (
              <button
                type="button"
                onClick={() => {
                  closeDetail();
                  openEditForm(detailCampaign);
                }}
                className={LISTING_PRIMARY_BUTTON_CLASS}
              >
                {t("ccEditCampaign")}
              </button>
            ) : null
          }
        >
          {detailCampaign ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <div className={FILTER_LABEL_CLASS}>{t("ccCampaignTypeLabel")}</div>
                        <div className="font-semibold text-slate-900">
                          {t("ccCampaignTypeAiPaymentReminder")}
                        </div>
                      </div>
                      <div>
                        <div className={FILTER_LABEL_CLASS}>{t("ccStatus")}</div>
                        <StatusBadge
                          status={detailCampaign.active ? "ACTIVE" : "CANCELLED"}
                          label={
                            detailCampaign.active
                              ? t("ccCampaignStatusActive")
                              : t("ccCampaignStatusInactive")
                          }
                        />
                      </div>
                      <div className="md:col-span-2">
                        <div className={FILTER_LABEL_CLASS}>{t("ccExecutionScheduleCron")}</div>
                        <div className="font-mono text-sm text-slate-800">
                          {detailCampaign.scheduleCron || t("ccEmDash")}
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 text-sm text-slate-700">
                      <p className="font-semibold text-slate-900">{t("ccCampaignOutcomeTitle")}</p>
                      <p className="mt-1 text-xs leading-5 text-slate-600">{t("ccCampaignOutcomeHint")}</p>
                    </div>

                    <div>
                      <h3 className="mb-3 text-sm font-semibold text-slate-800">
                        {t("ccCampaignRecentTasks")}
                      </h3>
                      {detailTasksLoading ? (
                        <TableSkeleton columns={5} rows={4} />
                      ) : !detailTasks.length ? (
                        <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                          {t("ccCampaignTasksEmpty")}
                        </p>
                      ) : (
                        <div className="overflow-x-auto rounded-xl border border-slate-200">
                          <table className="min-w-full text-sm">
                            <thead className={LISTING_TABLE_HEAD_CLASS}>
                              <tr>
                                <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccAccountNo")}</th>
                                <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccPhone")}</th>
                                <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccStatus")}</th>
                                <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccCampaignTaskAiOutcome")}</th>
                                <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccUpdatedAt")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {detailTasks.map((task) => (
                                <tr key={task.id} className={LISTING_TABLE_ROW_CLASS}>
                                  <td className="px-4 py-3 text-slate-800">
                                    {task.accountId || t("ccEmDash")}
                                  </td>
                                  <td className="px-4 py-3 text-slate-600">
                                    {task.phoneNumber || t("ccEmDash")}
                                  </td>
                                  <td className="px-4 py-3">
                                    <StatusBadge
                                      status={task.status || "PENDING"}
                                      label={formatCallTaskStatus(task.status, t)}
                                    />
                                  </td>
                                  <td className="px-4 py-3 text-slate-600">
                                    {formatAiCallIntent(task.aiCallIntent, t)}
                                  </td>
                                  <td className="px-4 py-3 text-slate-500">
                                    {formatTimestamp(task.updatedAt)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                  </div>
          ) : null}
        </CallCenterModal>
      </div>
    </div>
  );
};

export default CallCenterCampaignsPage;
